import { useState } from "react";
import { Button, controlToastState } from "risa-oasis-ui_v2";

import { updateAppeal } from "../../../../api/postCall/updateAppeal";
import { updateBigQueryData } from "../../../../api/postCall/updateBigQueryData";
import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { logError } from "../../../../utils/customLogger";
import {
  buildCallingFormDataFromOrder,
  CallingFormData,
  getCallingPocSelectOptions,
  getResponseStatusSelectOptions,
} from "../../../sidePanel/submissionSummarySidePanel/utils/buildCallingFormDataFromOrder";
import AppealSummaryTabBar from "../components/appealSummaryTabBar";
import CallSummaryFields from "../components/callSummaryFields";
import {
  getAppealStatusSelectOptions,
  resolveAppealStatusValue,
} from "../utils/appealStatusOptions";
import { buildAppealUpdatePayload } from "../utils/buildAppealUpdatePayload";
import {
  CallSummaryMode,
  CALL_SUMMARY_MODE_TABS,
} from "../utils/callSummaryMode";
import { getCurrentEstTimestamp } from "../utils/getCurrentEstTimestamp";
import { uploadFaxDocument } from "../utils/uploadFaxDocument";

interface CallSummaryPaneProps {
  data: PharmaStpFileModel;
  /** Active channel tab, owned by the modal so it can size the dialog. */
  mode: CallSummaryMode;
  onModeChange: (mode: CallSummaryMode) => void;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Fixed Second STP Outcome label (e.g. "Appeal" on Case Appeals). When set,
   * the field stays disabled and save uses this value instead of Call/Fax.
   */
  secondStpOutcomeLabel?: string;
}

/**
 * Splits the footer evenly between Cancel and Save. The oasis `Button` takes no
 * className, so the rendered element is stretched from its wrapper.
 */
const FOOTER_BUTTON_WRAPPER_CLASSES = "flex-1 [&>button]:w-full";

/**
 * Right pane of the appeal summary modal. Owns the form state shared by the
 * Call and Fax tabs, validates it, and persists the summary to the BigQuery
 * order row and the appeal record. Nothing is written to the Firestore calling
 * history from here — that log belongs to the calling worklist modal.
 *
 * @param props.data - Order row the modal was opened for
 * @param props.onClose - Closes the modal
 * @param props.onSuccess - Called after a successful save so the table refetches
 */
const CallSummaryPane = (props: CallSummaryPaneProps) => {
  const { data, mode } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [dateTime] = useState(() => getCurrentEstTimestamp());
  const [formData, setFormData] = useState<CallingFormData>(() => ({
    ...buildCallingFormDataFromOrder(data),
    // Status is the appeal's own workflow status, not the worklist case status.
    caseStatus: resolveAppealStatusValue(data?.appeal_status),
  }));
  const [faxDocument, setFaxDocument] = useState<File | null>(null);

  const pocOptions = getCallingPocSelectOptions(formData.callingPoc);
  const responseStatusOptions = getResponseStatusSelectOptions(
    formData.responseStatus,
  );
  const caseStatusOptions = getAppealStatusSelectOptions(formData.caseStatus);

  const handleChange = (field: keyof CallingFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFaxMode = mode === CallSummaryMode.FAX;
  const isContactValid = isFaxMode
    ? formData.faxNumber.trim() !== "" && formData.sentTo.trim() !== ""
    : formData.phoneNumber.trim() !== "" && formData.repName.trim() !== "";

  // Every field is required except Next Action and Follow Up Date. Second STP
  // Outcome is read-only and always set from the tab, so there is nothing to
  // check for it.
  const isFormValid =
    isContactValid &&
    formData.caseId.trim() !== "" &&
    formData.comment.trim() !== "" &&
    formData.responseStatus.trim() !== "" &&
    formData.callingPoc.trim() !== "" &&
    formData.caseStatus.trim() !== "" &&
    (!isFaxMode || faxDocument !== null);

  const handleSave = async () => {
    const identifier = data?.identifier;
    if (!identifier) {
      controlToastState("submission-summary-save-error");
      return;
    }

    setIsLoading(true);

    try {
      // Uploaded first so the appeal update can carry the stored document path.
      const attachment =
        isFaxMode && faxDocument
          ? await uploadFaxDocument(faxDocument, identifier)
          : null;

      // Second STP Outcome: fixed label when provided (e.g. Appeals), else Call/Fax tab.
      // Preserve label casing so Case Appeals writes "Appeal", not "appeal".
      const secondStpStatus = props.secondStpOutcomeLabel?.trim() || mode;

      // The two rows are independent: the appeal record takes the form, the PA
      // row takes the response and second STP statuses. Sequenced so a failure
      // is reported before the second write is attempted; both are idempotent
      // updates keyed by identifier, so retrying the save is safe.
      await updateAppeal(
        buildAppealUpdatePayload(identifier, formData, mode, attachment),
      );

      await updateBigQueryData({
        filters: { identifier },
        update_data: {
          response_status: formData.responseStatus,
          second_stp_status: secondStpStatus,
        },
      });

      controlToastState("submission-summary-save-success");
      props.onSuccess?.();
      props.onClose();
    } catch (error) {
      logError(error as Error, "Error saving calling summary");
      controlToastState("submission-summary-save-error");
    }
    setIsLoading(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <AppealSummaryTabBar
        tabs={CALL_SUMMARY_MODE_TABS}
        activeTabId={mode}
        onTabChange={(tabId) => props.onModeChange(tabId as CallSummaryMode)}
      />

      <div className="min-h-0 flex-1 p-4">
        <CallSummaryFields
          key={`${mode}-${props.secondStpOutcomeLabel ?? ""}`}
          mode={mode}
          formData={formData}
          dateTime={dateTime}
          secondStpOutcomeLabel={props.secondStpOutcomeLabel}
          pocOptions={pocOptions}
          responseStatusOptions={responseStatusOptions}
          caseStatusOptions={caseStatusOptions}
          faxDocument={faxDocument}
          onFaxDocumentChange={setFaxDocument}
          isSaving={isLoading}
          onChange={handleChange}
        />
      </div>

      <div className="flex w-full shrink-0 items-center gap-4 border-t border-primaryGray-15 px-6 py-4">
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="secondary"
            onClick={props.onClose}
            disabled={isLoading}
            size="medium"
          >
            Cancel
          </Button>
        </div>
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="primary"
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            size="medium"
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallSummaryPane;
